import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Copy, User, Bot } from 'lucide-react';
import { ArtifactRenderer } from '@/components/ArtifactRenderer';
import { FeedbackButtons } from '@/components/FeedbackButtons';
    import { MessageWithCitations } from '@/components/MessageWithCitations';
import { GroundingSources } from '@/components/GroundingSources';
import { useFeedback } from '@/hooks/useFeedback';
import { useBreakpoint } from '@/hooks/useMediaQuery';
import { extractSources } from '@/utils/groundingUtils';
import { cn } from '@/lib/utils';
import type { Message } from '@/types';

interface MessageBubbleProps {
  message: Message;
  onCopy?: (content: string) => void;
}

export function MessageBubble({ message, onCopy }: MessageBubbleProps) {
  const isUser = message.role === 'user';
  const isAssistant = message.role === 'assistant';
  const { toggleFeedback } = useFeedback();
  const { isMobile, isSmallMobile } = useBreakpoint();

  const handleCopy = () => {
    if (onCopy) {
      onCopy(message.content);
    } else {
      navigator.clipboard.writeText(message.content);
    }
  };

  const handleFeedback = async (messageId: string, type: 'like' | 'dislike') => {
    try {
      await toggleFeedback(messageId, type, message.feedback);
    } catch (error) {
      console.error('Failed to submit feedback:', error);
      // TODO: Show error toast notification
    }
  };

  // Check if content has bullet points or special formatting
  const hasBulletPoints = message.content.includes('*') || message.content.includes('•') || message.content.includes('-');
  const hasMultipleLines = message.content.includes('\n');

  // Extract sources for grounding panel
  const sources = isAssistant && message.groundingMetadata
    ? extractSources(message.groundingMetadata)
    : [];

  return (
    <div className={cn(
      'flex max-w-4xl mx-auto py-3',
      isMobile ? 'gap-2 px-2' : 'gap-3 px-4',
      isUser && 'flex-row-reverse'
    )}>
      {/* Avatar */}
      <div className={cn(
        'flex-shrink-0 rounded-full flex items-center justify-center',
        isMobile ? 'w-6 h-6' : 'w-8 h-8',
        isUser ? 'bg-primary text-primary-foreground' : 'bg-muted text-muted-foreground'
      )}>
        {isUser ? (
          <User className={isMobile ? 'h-3 w-3' : 'h-4 w-4'} />
        ) : (
          <Bot className={isMobile ? 'h-3 w-3' : 'h-4 w-4'} />
        )}
      </div>

      {/* Message Content */}
      <div className={cn(
        'flex-1 min-w-0',
        isUser && 'flex flex-col items-end'
      )}>
        {/* Message Header */}
        <div className={cn(
          'flex items-center mb-1',
          isMobile ? 'gap-1' : 'gap-2',
          isUser && 'flex-row-reverse'
        )}>
          <span className={cn(
            'font-medium',
            isMobile ? 'text-xs' : 'text-sm'
          )}>
            {isUser ? 'You' : 'Assistant'}
          </span>
          <span className="text-xs text-muted-foreground">
            {message.timestamp.toLocaleTimeString([], { 
              hour: '2-digit', 
              minute: '2-digit' 
            })}
          </span>
        </div>

        {/* Message Bubble */}
        <Card className={cn(
          isMobile ? 'max-w-[85%]' : 'max-w-[80%]',
          isUser ? 'bg-primary text-primary-foreground' : 'bg-muted/50',
          'overflow-hidden'
        )}>
          <CardContent className={cn(
            isMobile ? 'p-2' : 'p-3',
            'overflow-hidden'
          )}>
            {/* Enhanced content formatting for assistant messages */}
            {isAssistant && (hasBulletPoints || hasMultipleLines || message.groundingMetadata) ? (
              <div className="max-w-full overflow-hidden break-words">
                <MessageWithCitations message={message} />
              </div>
            ) : (
              <div className={cn(
                'prose max-w-none dark:prose-invert overflow-hidden',
                isMobile ? 'prose-xs' : 'prose-sm'
              )}>
                <p className="whitespace-pre-wrap break-words m-0 text-sm leading-relaxed overflow-hidden">
                  {message.content}
                </p>
              </div>
            )}

            {/* Grounding Sources */}
            {sources.length > 0 && (
              <GroundingSources sources={sources} />
            )}

            {/* Artifacts */}
            {message.artifacts && message.artifacts.length > 0 && (
              <div className={cn(
                'space-y-3',
                isMobile ? 'mt-2' : 'mt-4'
              )}>
                {message.artifacts.map((artifact, index) => (
                  <ArtifactRenderer key={index} artifact={artifact} />
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Message Actions */}
        {isAssistant && (
          <div className={cn(
            'flex items-center gap-1',
            isMobile ? 'mt-1' : 'mt-2'
          )}>
            <Button
              variant="ghost"
              size="sm"
              className={cn(
                'px-2 text-xs',
                isMobile ? 'h-5' : 'h-6'
              )}
              onClick={handleCopy}
            >
              <Copy className={cn(
                'mr-1',
                isMobile ? 'h-2.5 w-2.5' : 'h-3 w-3'
              )} />
              {!isSmallMobile && 'Copy'}
            </Button>
            
            <FeedbackButtons
              messageId={message.id}
              feedback={message.feedback}
              onFeedback={handleFeedback}
            />
          </div>
        )}
      </div>
    </div>
  );
}
